import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useFormik } from "formik";
import React, { useCallback, useEffect, useState } from "react";
import { Label } from "reactstrap";
import InputCustom from "../../../../../../components/InputCustom";
import SelectCustom from "../../../../../../components/SelectCustom";
import Loading from "../../../../../../core/common/Loading";
import api from "../../../../../../services/api";
import { FrontendNotification } from "../../../../../../shared/Notification";
import { TipoVeiculo } from "../../../../Cadastro/Veiculos/types/types";
import { useAuth } from "../../../../../../hooks/AuthenticateContext";
import formValidator from "../../../OperacoesPatioEntradaVeiculos/Create/validators/formValidator";

interface Props {
  isView?: boolean;
  isEdit?: boolean;
  selectedRow?: any;
  onConfirm: () => void;
  onClose: () => void;
}

interface FormValues {
  data_hora: string;
  placa_dianteira: string;
  placa_traseira: string;
  numero_partes_nao_motorizada: string;
  identificadores_conteineres: string;
  id_operacao_patio_cancela: string;
  id_operacao_patio_cancela_saida: string;
  data_hora_saida: string;
}

const Form: React.FC<Props> = (props: Props) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [cancelasEntrada, setCancelasEntrada] = useState<{ label: string; value: any }[]>([]);
  const [cancelasSaida, setCancelasSaida] = useState<{ label: string; value: any }[]>([]);
  const [conteiners, setConteiners] = useState({
    conteiners1: "",
    conteiners2: "",
    conteiners3: "",
    conteiners4: "",
  });
  const [vehicleTypes, setVehicleTypes] = useState<any[]>([]);
  const { user } = useAuth();

  const handleSubmit = useCallback(
    async (
      values: FormValues,
      row?: any,
      listConteiners?: any
    ) => {
      try {
        setLoading(true);

        let getIndentificadorConteiner = "{";

        if (listConteiners?.conteiners1?.length > 0) {
          getIndentificadorConteiner += `${listConteiners.conteiners1},`;
        }
        if (listConteiners?.conteiners2?.length > 0) {
          getIndentificadorConteiner += `${listConteiners.conteiners2},`;
        }
        if (listConteiners?.conteiners3?.length > 0) {
          getIndentificadorConteiner += `${listConteiners.conteiners3},`;
        }
        if (listConteiners?.conteiners4?.length > 0) {
          getIndentificadorConteiner += `${listConteiners.conteiners4},`;
        }

        getIndentificadorConteiner += "}";
        const conteinersStr = getIndentificadorConteiner.length > 3
          ? getIndentificadorConteiner.replace(",}", "}")
          : null;

        const bodyEntrada: any = {
          id_operacao_patio_entrada_veiculo: row?.id_operacao_patio_entrada_veiculo_SPE || row?.id_operacao_patio_entrada_veiculo,
          data_hora: values.data_hora
            ? format(new Date(values.data_hora), "yyyy-MM-dd HH:mm:ss", { locale: ptBR })
            : null,
          placa_dianteira: values.placa_dianteira ? values.placa_dianteira.replace("-", "").toUpperCase() : "",
          placa_traseira: values.placa_traseira && values.placa_traseira.length > 0
            ? values.placa_traseira.replace("-", "").toUpperCase()
            : null,
          numero_partes_nao_motorizada: values.numero_partes_nao_motorizada || null,
          identificadores_conteineres: conteinersStr,
          id_operacao_patio_cancela: values.id_operacao_patio_cancela ? Number(values.id_operacao_patio_cancela) : null,
          ativo: true,
          id_usuario_historico: user?.id,
          status: 1,
        };

        let entradaId = row?.id_operacao_patio_entrada_veiculo_SPE || row?.id_operacao_patio_entrada_veiculo;

        if (props.isEdit && entradaId) {
          await api.post("/atualizar/entradaVeiculosSpe", bodyEntrada);
        } else {
          const resp = await api.post("/cadastrar/entradaVeiculosSpe", bodyEntrada);
          entradaId = resp.data?.id_operacao_patio_entrada_veiculo_SPE || resp.data?.id_operacao_patio_entrada_veiculo;
        }

        // Se dados de saída forem preenchidos
        if (values.data_hora_saida || values.id_operacao_patio_cancela_saida) {
          const bodySaida: any = {
            id_operacao_patio_saida_veiculo: row?.saida?.id_operacao_patio_saida_veiculo_SPE || row?.saida?.id_operacao_patio_saida_veiculo,
            id_operacao_patio_entrada_veiculo: entradaId,
            data_hora: values.data_hora_saida
              ? format(new Date(values.data_hora_saida), "yyyy-MM-dd HH:mm:ss", { locale: ptBR })
              : null,
            placa_dianteira: values.placa_dianteira ? values.placa_dianteira.replace("-", "").toUpperCase() : "",
            placa_traseira: values.placa_traseira && values.placa_traseira.length > 0
              ? values.placa_traseira.replace("-", "").toUpperCase()
              : null,
            id_operacao_patio_cancela: values.id_operacao_patio_cancela_saida ? Number(values.id_operacao_patio_cancela_saida) : null,
            ativo: true,
            id_usuario_historico: user?.id,
          };

          if (row?.saida?.id_operacao_patio_saida_veiculo_SPE || row?.saida?.id_operacao_patio_saida_veiculo) {
            await api.post("/atualizar/saidaVeiculosSpe", bodySaida);
          } else {
            await api.post("/cadastrar/saidaVeiculosSpe", bodySaida);
          }
        }

        setLoading(false);
        FrontendNotification("Operação SPE realizada com sucesso!", "success");
        props.onConfirm();
      } catch (error: any) {
        setLoading(false);
        const msg = error?.response?.data?.error_msg || "Erro ao salvar a entrada/saída SPE.";
        FrontendNotification(msg, "error");
      }
    },
    [props, user]
  );

  const getCancelasEntradas = useCallback(async () => {
    try {
      setLoading(true);
      let response = await api.post("/listar/cancelasEntradaSpe", {}).catch(() => null);
      if (!response || !response.data || response.data.length === 0) {
        response = await api.post("/listar/cancelasEntrada", {});
      }
      const data = response?.data?.data || response?.data || [];
      const mappingResponse = data.map((item: any) => ({
        label: `Cancela ${item.numero_cancela}`,
        value: item.id_operacao_patio_cancela,
      }));
      setCancelasEntrada(mappingResponse);
      setLoading(false);
    } catch {
      setLoading(false);
    }
  }, []);

  const getCancelasSaida = useCallback(async () => {
    try {
      setLoading(true);
      let response = await api.post("/listar/cancelasSaidaSpe", {}).catch(() => null);
      if (!response || !response.data || response.data.length === 0) {
        response = await api.post("/listar/cancelasSaida", {});
      }
      const data = response?.data?.data || response?.data || [];
      const mappingResponse = data.map((item: any) => ({
        label: `Cancela ${item.numero_cancela}`,
        value: item.id_operacao_patio_cancela,
      }));
      setCancelasSaida(mappingResponse);
      setLoading(false);
    } catch {
      setLoading(false);
    }
  }, []);

  const getVehicleTypes = useCallback(() => {
    const data = Object.values(TipoVeiculo).map((value: any, index: number) => ({
      value: `${index}`,
      label: value,
    }));
    setVehicleTypes(data);
  }, []);

  const onLoadFormValues = useCallback(
    (row?: any) => {
      const data = row;

      if (data) {
        formik.setFieldValue("data_hora", data.data_hora ? format(new Date(data.data_hora), "yyyy-MM-dd'T'HH:mm") : "");
        formik.setFieldValue("placa_dianteira", data.placa_dianteira ?? "");
        formik.setFieldValue("placa_traseira", data.placa_traseira ?? "");
        formik.setFieldValue(
          "numero_partes_nao_motorizada",
          data.numero_partes_nao_motorizada ?? ""
        );
        formik.setFieldValue(
          "id_operacao_patio_cancela",
          data.id_operacao_patio_cancela ?? ""
        );

        if (
          data.identificadores_conteineres !== null &&
          data.identificadores_conteineres !== undefined &&
          data.identificadores_conteineres.length > 0
        ) {
          const getConteineres = data.identificadores_conteineres
            .replace("{", "")
            .replace("}", "");

          const mappingConteiners = getConteineres.split(",");

          setConteiners({
            conteiners1: mappingConteiners[0] ? mappingConteiners[0] : "",
            conteiners2: mappingConteiners[1] ? mappingConteiners[1] : "",
            conteiners3: mappingConteiners[2] ? mappingConteiners[2] : "",
            conteiners4: mappingConteiners[3] ? mappingConteiners[3] : "",
          });
        }

        if (data.saida) {
          formik.setFieldValue("id_operacao_patio_cancela_saida", data.saida.id_operacao_patio_cancela ?? "");
          formik.setFieldValue("data_hora_saida", data.saida.data_hora ? format(new Date(data.saida.data_hora), "yyyy-MM-dd'T'HH:mm") : "");
        } else {
          formik.setFieldValue("id_operacao_patio_cancela_saida", "");
          formik.setFieldValue("data_hora_saida", "");
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const initialValues: FormValues = {
    data_hora: "",
    placa_dianteira: "",
    placa_traseira: "",
    numero_partes_nao_motorizada: "",
    identificadores_conteineres: "",
    id_operacao_patio_cancela: "",
    id_operacao_patio_cancela_saida: "",
    data_hora_saida: "",
  };

  const formik = useFormik({
    initialValues,
    validationSchema: formValidator,
    onSubmit: (values: FormValues) =>
      handleSubmit(values, props.selectedRow, conteiners),
  });

  useEffect(() => {
    getCancelasEntradas();
    getCancelasSaida();
    getVehicleTypes();
  }, [getCancelasEntradas, getCancelasSaida, getVehicleTypes]);

  useEffect(() => {
    if (props.isView || props.isEdit) {
      onLoadFormValues(props.selectedRow);
    }
  }, [props.isView, props.isEdit, props.selectedRow, onLoadFormValues]);

  return (
    <>
      <Loading loading={loading} skeletonFields={11} skeletonColumns={3} />
      <div className="overflow-y-scroll max-h-[calc(80vh-80px)] p-5">
        <div className="mb-3">
          <span className="text-sm font-bold">Entrada</span>
          <div className="w-full h-[1px] bg-[#ccc] mt-2" />
        </div>
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div>
            <InputCustom
              id="dataHoraEntrada"
              title="Data Hora da Entrada"
              type="datetime-local"
              placeholder=""
              onChange={formik.handleChange("data_hora")}
              value={formik.values.data_hora}
              touched={formik.touched.data_hora}
              error={formik.errors.data_hora}
              disabled={props.isView}
            />
          </div>
          <div>
            <InputCustom
              id="placaDianteira"
              title="Placa Dianteira"
              placeholder="AAA-0000 OU AAA-0A00"
              typeInput="mask"
              mask="aaa-9*99"
              onChange={formik.handleChange("placa_dianteira")}
              value={formik.values.placa_dianteira}
              touched={formik.touched.placa_dianteira}
              error={formik.errors.placa_dianteira}
              disabled={props.isView}
            />
          </div>
          <div>
            <InputCustom
              id="placaTraseira"
              title="Placa Traseira"
              placeholder="AAA-0000 OU AAA-0A00"
              typeInput="mask"
              mask="aaa-9*99"
              onChange={formik.handleChange("placa_traseira")}
              value={formik.values.placa_traseira}
              touched={formik.touched.placa_traseira}
              error={formik.errors.placa_traseira}
              disabled={props.isView}
              isRequired={false}
            />
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3 mb-2">
          <div>
            <SelectCustom
              id="tipoVeiculo"
              data={vehicleTypes}
              onChange={(selectedOption: any) => {
                formik.setFieldValue("numero_partes_nao_motorizada", selectedOption?.value || "");
              }}
              title="Tipo do Veículo"
              touched={formik.touched.numero_partes_nao_motorizada}
              error={formik.errors.numero_partes_nao_motorizada}
              disabled={props.isView}
              value={formik.values.numero_partes_nao_motorizada}
            />
          </div>
          <div>
            <Label
              style={{
                fontFamily: "Poppins",
                fontSize: "0.875rem",
                color: "#000000",
                fontWeight: 500,
                display: "flex",
                flexDirection: "column",
                marginBottom: "0.400rem !important",
              }}
            >
              Identificadores dos Contêineres
            </Label>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <InputCustom
                  id="identificadoresConteineres1"
                  title=""
                  typeInput="mask"
                  mask="aaaa-9999999"
                  placeholder="AAAA0000000"
                  onChange={(value: string) =>
                    setConteiners({
                      ...conteiners,
                      conteiners1: value,
                    })
                  }
                  value={conteiners.conteiners1}
                  disabled={props.isView}
                  isRequired={false}
                />
              </div>
              <div>
                <InputCustom
                  id="identificadoresConteineres2"
                  title=""
                  typeInput="mask"
                  mask="aaaa-9999999"
                  placeholder="AAAA0000000"
                  onChange={(value: string) =>
                    setConteiners({
                      ...conteiners,
                      conteiners2: value,
                    })
                  }
                  value={conteiners.conteiners2}
                  disabled={props.isView}
                  isRequired={false}
                />
              </div>
              <div>
                <InputCustom
                  id="identificadoresConteineres3"
                  title=""
                  typeInput="mask"
                  mask="aaaa-9999999"
                  placeholder="AAAA0000000"
                  onChange={(value: string) =>
                    setConteiners({
                      ...conteiners,
                      conteiners3: value,
                    })
                  }
                  value={conteiners.conteiners3}
                  disabled={props.isView}
                  isRequired={false}
                />
              </div>
              <div>
                <InputCustom
                  id="identificadoresConteineres4"
                  title=""
                  typeInput="mask"
                  mask="aaaa-9999999"
                  placeholder="AAAA0000000"
                  onChange={(value: string) =>
                    setConteiners({
                      ...conteiners,
                      conteiners4: value,
                    })
                  }
                  value={conteiners.conteiners4}
                  disabled={props.isView}
                  isRequired={false}
                />
              </div>
            </div>
          </div>
          <div>
            <SelectCustom
              id="id_operacao_patio_cancela"
              data={cancelasEntrada}
              onChange={(selectedOption: any) => {
                formik.setFieldValue(
                  "id_operacao_patio_cancela",
                  selectedOption?.value || ""
                );
              }}
              title="Cancela Entrada"
              touched={formik.touched.id_operacao_patio_cancela}
              error={formik.errors.id_operacao_patio_cancela}
              disabled={props.isView}
              value={formik.values.id_operacao_patio_cancela}
            />
          </div>
        </div>

        <div className="mb-4 mt-6">
          <span className="text-sm font-bold">Saída</span>
          <div className="w-full h-[1px] bg-[#ccc] mt-2" />
        </div>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <SelectCustom
              id="id_operacao_patio_cancela_saida"
              data={cancelasSaida}
              onChange={(selectedOption: any) => {
                formik.setFieldValue(
                  "id_operacao_patio_cancela_saida",
                  selectedOption?.value || ""
                );
              }}
              title="Cancela Saída"
              touched={formik.touched.id_operacao_patio_cancela_saida}
              error={formik.errors.id_operacao_patio_cancela_saida}
              disabled={props.isView}
              value={formik.values.id_operacao_patio_cancela_saida}
            />
          </div>
          <div>
            <InputCustom
              id="data_hora_saida"
              title="Data Hora da Saída"
              type="datetime-local"
              placeholder=""
              onChange={formik.handleChange("data_hora_saida")}
              value={formik.values.data_hora_saida}
              touched={formik.touched.data_hora_saida}
              error={formik.errors.data_hora_saida}
              disabled={props.isView}
            />
          </div>
        </div>
      </div>

      <div className="sticky bottom-0 z-[999999999px] w-full h-14 flex items-center justify-end bg-[#FFFFFF] shadow-xl">
        <button
          type="button"
          className="w-24 h-9 pl-3 pr-3 flex items-center justify-center bg-[#F9FAFA] text-sm text-[#000000] font-bold rounded-full mr-2"
          style={{ border: "1px solid #DBDEDF" }}
          onClick={props.onClose}
          id="btnCancel"
        >
          Cancelar
        </button>
        <button
          type="button"
          className="w-24 h-9 pl-3 pr-3 flex items-center justify-center bg-[#0A4984] text-sm text-[#fff] font-bold rounded-full mr-2"
          onClick={() => formik.handleSubmit()}
          id="btnSave"
        >
          Salvar
        </button>
      </div>
    </>
  );
};

export default Form;
